import { useState, useEffect } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { ROLE_LABELS } from "@/lib/constants";
import { ThemeToggle } from "@/components/ThemeToggle";
import { NotificationBanners } from "@/components/NotificationBanners";
import { SiteFooter } from "@/components/SiteFooter";
import { MemberNotices } from "@/components/MemberNotices";
import { MemberTypePrompt } from "@/components/MemberTypePrompt";
import { CustomFieldsPrompt } from "@/components/CustomFieldsPrompt";
import { Bell } from "lucide-react";
import {
  SquaresFour, Users, CheckCircle, UserCircle, SignOut, Buildings, PersonSimpleWalk, NotePencil, Archive, TShirt, List, X, HandHeart, ChatCenteredText, CaretDown, UserCheck, Gift,
} from "@phosphor-icons/react";

const REGIONAL_UP = ["regional_manager", "national_manager", "super_admin"];
const NATIONAL = ["national_manager", "super_admin"];

const navFor = (role) => {
  if (role === "member") {
    return [{ to: "/profile", label: "My Membership", icon: UserCircle }, { to: "/my-souvenirs", label: "My Souvenirs", icon: Gift }, { to: "/welfare", label: "Welfare", icon: HandHeart }, { to: "/complaints", label: "Complaints", icon: ChatCenteredText }];
  }
  const items = [{ to: "/dashboard", label: "Dashboard", icon: SquaresFour }];
  const memberChildren = [{ to: "/members", label: "Members List", icon: Users }];
  if (NATIONAL.includes(role)) memberChildren.push({ to: "/retirees", label: "Retirees", icon: PersonSimpleWalk });
  if (REGIONAL_UP.includes(role)) memberChildren.push({ to: "/archived-members", label: "Archived", icon: Archive });
  if (role !== "chapter_manager") memberChildren.push({ to: "/welfare", label: "Welfare", icon: HandHeart });
  memberChildren.push({ to: "/complaints", label: "Complaints", icon: ChatCenteredText });
  items.push({ key: "members", label: "Members", icon: Users, children: memberChildren });
  if (REGIONAL_UP.includes(role)) {
    items.push({ to: "/notifications", label: "Notifications", icon: Bell });
    const children = [{ to: "/approvals", label: "Membership Approvals", icon: UserCheck }];
    if (NATIONAL.includes(role)) children.push({ to: "/edit-requests", label: "Profile Approvals", icon: NotePencil });
    items.push({ key: "approvals", label: "Approvals", icon: CheckCircle, children });
  }
  if (REGIONAL_UP.includes(role)) items.push({ to: "/organization", label: "Organization", icon: Buildings });
  items.push({ to: "/souvenirs", label: "Souvenirs", icon: TShirt });
  items.push({ to: "/profile", label: "My Member Data", icon: UserCircle });
  if (role !== "super_admin") items.push({ to: "/my-souvenirs", label: "My Souvenirs", icon: Gift });
  return items;
};

const linkCls = (isActive, nested = false) =>
  `flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors duration-200 ${nested ? "py-2" : ""} ${
    isActive
      ? "bg-white text-[hsl(var(--sidebar-bg))] shadow-sm"
      : "text-[hsl(var(--sidebar-fg))]/85 hover:bg-white/10 hover:text-[hsl(var(--sidebar-fg))]"
  }`;

const Badge = ({ count, testId }) => count > 0 ? (
  <span data-testid={testId}
    className="ml-auto inline-flex min-w-5 h-5 items-center justify-center rounded-full bg-amber-400 px-1.5 text-xs font-bold text-black">
    {count}
  </span>
) : null;

function NavGroup({ item, counts = {}, onNavigate }) {
  const { pathname } = useLocation();
  const childActive = item.children.some((c) => pathname === c.to || pathname.startsWith(`${c.to}/`));
  const [open, setOpen] = useState(childActive);
  useEffect(() => { if (childActive) setOpen(true); }, [childActive]);
  return (
    <div>
      <button type="button" onClick={() => setOpen((o) => !o)} data-testid={`nav-group-${item.key}`} aria-expanded={open}
        className={linkCls(false) + " w-full"}>
        <item.icon size={18} weight="bold" />
        <span className="flex-1 text-left">{item.label}</span>
        {!open && <Badge count={item.children.reduce((s, c) => s + (counts[c.to] || 0), 0)} testId={`${item.key}-group-badge`} />}
        <CaretDown size={14} weight="bold" className={`transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="mt-1 ml-5 pl-3 border-l border-white/25 space-y-1" data-testid={`nav-group-${item.key}-items`}>
          {item.children.map((c) => (
            <NavLink key={c.to} to={c.to} onClick={onNavigate} data-testid={`nav-${c.to.replace("/", "")}`}
              className={({ isActive }) => linkCls(isActive, true)}>
              <c.icon size={16} weight="bold" />
              <span className="flex-1">{c.label}</span>
              <Badge count={counts[c.to] || 0} testId={`${c.to.replace("/", "")}-badge`} />
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}

const Brand = ({ testId, onClick }) => (
  <Link to="/" onClick={onClick} data-testid={testId} aria-label="MELPWU home" className="flex items-center gap-2.5 rounded-md transition-opacity duration-200 hover:opacity-90">
    <img src="/org_logo.png" alt="MELPWU logo" className="h-9 w-9 rounded-full bg-white p-0.5 object-contain ring-2 ring-white/40" />
    <div className="leading-tight">
      <span className="block font-display text-lg font-bold tracking-tight">MELPWU</span>
      <span className="block text-[10px] uppercase tracking-[0.18em] opacity-75">Members Portal</span>
    </div>
  </Link>
);

export function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const items = navFor(user.role);
  const [counts, setCounts] = useState({});
  const [souvenirCount, setSouvenirCount] = useState(0);

  useEffect(() => {
    if (user.role === "super_admin") return;
    const load = () => api.get("/me/souvenirs")
      .then((r) => setSouvenirCount(r.data.filter((g) => g.status !== "received").length)).catch(() => {});
    load();
    const t = setInterval(load, 60000);
    window.addEventListener("souvenirs-updated", load);
    return () => { clearInterval(t); window.removeEventListener("souvenirs-updated", load); };
  }, [user.role]);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    if (!mobileOpen) return;
    const onKey = (e) => { if (e.key === "Escape") setMobileOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mobileOpen]);

  useEffect(() => {
    if (REGIONAL_UP.includes(user.role)) {
      const load = () => api.get("/approvals/count").then((r) => setCounts({
        "/approvals": r.data.total || 0, "/edit-requests": r.data.profile || 0,
      })).catch(() => {});
      load();
      const t = setInterval(load, 30000);
      return () => clearInterval(t);
    }
  }, [user.role]);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const close = () => setMobileOpen(false);
  const navLinks = (
    <nav className="flex-1 px-3 py-5 space-y-1 overflow-y-auto">
      {items.map((it) => it.children ? (
        <NavGroup key={it.key} item={it} counts={counts} onNavigate={close} />
      ) : (
        <NavLink key={it.to} to={it.to} onClick={close} data-testid={`nav-${it.to.replace("/", "")}`}
          className={({ isActive }) => linkCls(isActive)}>
          <it.icon size={18} weight="bold" />
          <span className="flex-1">{it.label}</span>
          {it.to === "/my-souvenirs" && <Badge count={souvenirCount} testId="my-souvenirs-badge" />}
        </NavLink>
      ))}
    </nav>
  );

  const userFooter = (
    <div className="border-t border-white/15 p-4">
      <div className="mb-3">
        <p className="text-sm font-semibold truncate">{user.first_name} {user.last_name}</p>
        <p className="text-xs opacity-75">{ROLE_LABELS[user.role]}</p>
      </div>
      <button onClick={handleLogout} data-testid="logout-button"
        className="flex w-full items-center gap-2 rounded-md border border-white/30 px-3 py-2 text-sm font-medium transition-colors duration-200 hover:bg-white hover:text-[hsl(var(--sidebar-bg))]">
        <SignOut size={18} weight="bold" /> Sign out
      </button>
    </div>
  );

  const sidebarCls = "flex-col bg-[hsl(var(--sidebar-bg))] text-[hsl(var(--sidebar-fg))]";

  return (
    <div className="flex min-h-screen bg-background">
      <aside className={`hidden md:flex w-64 sticky top-0 h-screen ${sidebarCls}`}>
        <div className="flex items-center px-5 h-16 border-b border-white/15"><Brand testId="sidebar-logo-home-link" /></div>
        <div className="h-1 brand-rule" />
        {navLinks}
        {userFooter}
      </aside>

      {mobileOpen && (
        <div className="fixed inset-0 z-40 md:hidden" data-testid="mobile-menu-overlay">
          <div className="absolute inset-0 bg-black/50" onClick={close} />
          <aside className={`absolute left-0 top-0 h-full w-72 max-w-[80%] flex shadow-xl ${sidebarCls}`} data-testid="mobile-sidebar">
            <div className="flex items-center justify-between px-4 h-16 border-b border-white/15">
              <Brand testId="drawer-logo-home-link" onClick={close} />
              <button onClick={close} data-testid="mobile-menu-close" aria-label="Close navigation menu" className="p-1 opacity-80 hover:opacity-100">
                <X size={22} weight="bold" />
              </button>
            </div>
            {navLinks}
            {userFooter}
          </aside>
        </div>
      )}

      <div className="flex-1 flex flex-col min-w-0">
        <div className="sticky top-0 z-20 shadow-sm">
          <NotificationBanners />
          <header className="flex h-16 items-center justify-between gap-4 bg-[hsl(var(--header-bg))] text-[hsl(var(--header-fg))] px-4 md:px-8">
            <div className="flex items-center gap-2 md:hidden">
              <button onClick={() => setMobileOpen(true)} data-testid="mobile-menu-button" aria-label="Open navigation menu" className="p-1.5 -ml-1">
                <List size={24} weight="bold" />
              </button>
              <Brand testId="header-logo-home-link" />
            </div>
            <div className="hidden md:block">
              <span className="text-xs uppercase tracking-[0.2em] opacity-85" data-testid="console-label">
                {ROLE_LABELS[user.role]} Console
              </span>
            </div>
            <div className="flex items-center gap-3">
              <ThemeToggle className="border-white/30 bg-white/10 text-white hover:bg-white hover:text-[hsl(var(--header-bg))]" />
            </div>
          </header>
          <div className="h-1 brand-rule" />
        </div>
        <main className="flex-1 p-4 md:p-8 max-w-[1400px] w-full">{user.role !== "super_admin" && <><MemberNotices /><MemberTypePrompt /><CustomFieldsPrompt /></>}{children}</main>
        <SiteFooter />
      </div>
    </div>
  );
}
